import React, { forwardRef, HTMLAttributes, ReactElement } from "react";
import classNames from "classnames";

import styles from "./TreeItem.module.css";
import CaretDown from "~icons/fa6-solid/caret-down";
import { Handle } from "./Handle";
import { Action } from "./Action";
import { Remove } from "./Remove";

export interface Props extends Omit<HTMLAttributes<HTMLLIElement>, "id"> {
  childCount?: number;
  clone?: boolean;
  collapsed?: boolean;
  depth: number;
  disableInteraction?: boolean;
  disableSelection?: boolean;
  ghost?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  handleProps?: any;
  indicator?: boolean;
  indentationWidth: number;
  value: ReactElement;
  onCollapse?(): void;
  onRemove?(): void;
  wrapperRef?(node: HTMLLIElement): void;
}

export const TreeItem = forwardRef<HTMLDivElement, Props>(
  (
    {
      childCount,
      clone,
      depth,
      disableSelection,
      disableInteraction,
      ghost,
      handleProps,
      indentationWidth,
      indicator,
      collapsed,
      onCollapse,
      onRemove,
      style,
      value,
      wrapperRef,
      ...props
    },
    ref,
  ) => {
    return (
      <li
        className={classNames(
          styles.Wrapper,
          clone && styles.clone,
          ghost && styles.ghost,
          indicator && styles.indicator,
          disableSelection && styles.disableSelection,
          disableInteraction && styles.disableInteraction,
        )}
        ref={wrapperRef}
        style={
          {
            "--tree-spacing": `${indentationWidth * depth}px`,
          } as React.CSSProperties
        }
        {...props}
      >
        <div className={styles.TreeItem} ref={ref} style={style}>
          <Handle {...handleProps} />
          {onCollapse ? (
            <Action
              onClick={(e) => {
                e.preventDefault();
                onCollapse();
              }}
              className={classNames(
                styles.Collapse,
                collapsed && styles.collapsed,
                "cursor-pointer w-10 shrink-0 flex items-center justify-center text-slate-400 hover:text-slate-600",
              )}
            >
              <CaretDown />
            </Action>
          ) : (
            <div className="w-10 shrink-0" />
          )}
          {value}
          {!clone && onRemove && (
            <Remove
              onClick={(e) => {
                e.preventDefault();
                onRemove();
              }}
            />
          )}
          {clone && childCount && childCount > 1 ? (
            <span className={styles.Count}>{childCount}</span>
          ) : null}
        </div>
      </li>
    );
  },
);
